import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../Config/config";
import { auth } from "../Config/firebase";
import {
  checkDocsPermission,
  stripHtml,
} from "./infoShared";
import DocContentEditor from "./DocContentEditor";

const InfoCreate = () => {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [permissionLoading, setPermissionLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  useEffect(() => {
    const run = async () => {
      const permission = await checkDocsPermission();
      setHasPermission(permission.allowed);
      setPermissionError(permission.error);
      setPermissionLoading(false);
    };

    run();
  }, []);

  const summary = useMemo(() => {
    const plain = stripHtml(description);
    const words = plain ? plain.split(" ").filter(Boolean).length : 0;
    return {
      words,
      chars: plain.length,
    };
  }, [description]);

  const canSubmit = title.trim().length > 0 && !saving;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      const payload = {
        title: title.trim(),
        description,
        createdBy: auth.currentUser?.uid || "",
      };

      await axios.post(`${config.API_BASE_URL}/docs`, payload);
      navigate("/docs");
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (permissionLoading) {
    return (
      <div className="flex justify-center mt-10">
        <div className="px-4 py-3 rounded-xl border border-slate-200 bg-white/90 text-slate-700 text-sm shadow-sm">
          Checking permissions...
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex justify-center mt-10 px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          {permissionError || "Access denied."}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <section className="lg:col-span-2 rounded-2xl border border-slate-200/90 bg-white/95 shadow-[0_18px_40px_rgba(15,23,42,0.10)] p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Create Document
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Build a doc with rich content. Add images directly inside the editor.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/docs")}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Back to Docs
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
                placeholder="Write a clear document title"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-500"
              />
            </div>

            <DocContentEditor
              label="Content"
              value={description}
              onChange={setDescription}
            />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
              <p className="text-xs text-slate-500">
                {summary.words} words • {summary.chars} characters
              </p>
              <button
                type="submit"
                disabled={!canSubmit}
                className={`inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all ${
                  canSubmit
                    ? "bg-slate-900 hover:bg-slate-800"
                    : "bg-slate-400 cursor-not-allowed"
                }`}
              >
                {saving ? "Creating..." : "Create Document"}
              </button>
            </div>
          </form>
        </section>

        <aside className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-sky-50 via-white to-slate-100 shadow-sm p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
            Editor Guide
          </h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>Use headings to break long instructions into chunks.</li>
            <li>Insert screenshots directly with the Quill image button.</li>
            <li>Use numbered lists for step-by-step setup flows.</li>
            <li>Keep the first paragraph short so previews stay readable.</li>
          </ul>
        </aside>
      </div>
    </div>
  );
};

export default InfoCreate;
